import React, { useEffect, useState } from "react";
import useSalonStore from "./../../store/salonStore";
import useAuthStore from "../../store/authStore";
import { isManager } from "../../utils/permissions";
import {
  FiEdit3,
  FiSave,
  FiX,
  FiClock,
  FiCalendar,
  FiPauseCircle,
  FiUsers,
  FiUser,
  FiWatch,
  FiMapPin,
  FiInfo,
  FiPlus,
  FiTrash2,
} from "react-icons/fi";

function formatFrenchDateCapitalized(isoDate) {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  const formatted = new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

function formatTimeForInput(timeString) {
  if (!timeString) return "";
  return timeString.includes(":") ? timeString.slice(0, 5) : timeString;
}

function formatSpecialDate(dateString) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString + "T00:00:00");
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date);
  } catch {
    return dateString;
  }
}

// Composant pour l'input de date avec calendrier
const DatePicker = ({ value, onChange, placeholder }) => {
  return (
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-nunito text-[14px]"
      placeholder={placeholder}
      min={new Date().toISOString().split("T")[0]} // Empêcher les dates passées
    />
  );
};

// Lundi → dimanche ; day = numéro JavaScript (0 = dimanche)
const WEEK_DAYS = [
  { day: 1, label: "Lundi" },
  { day: 2, label: "Mardi" },
  { day: 3, label: "Mercredi" },
  { day: 4, label: "Jeudi" },
  { day: 5, label: "Vendredi" },
  { day: 6, label: "Samedi" },
  { day: 0, label: "Dimanche" },
];

const dayLabel = (day) => WEEK_DAYS.find((w) => w.day === day).label;

const toMinutes = (time) => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const normalizeSchedule = (schedule) =>
  WEEK_DAYS.map(({ day }) => {
    const d = schedule?.find((item) => item.day === day) || {};
    return {
      day,
      isOpen: d.isOpen ?? true,
      openingTime: formatTimeForInput(d.openingTime) || "09:00",
      closingTime: formatTimeForInput(d.closingTime) || "18:00",
      breakStart: formatTimeForInput(d.breakStart) || "",
      breakEnd: formatTimeForInput(d.breakEnd) || "",
    };
  });

// Mêmes règles que l'API (utils/salonSchedule.util.ts)
const validateSchedule = (schedule) => {
  const errors = [];
  schedule.forEach((d) => {
    if (!d.isOpen) return;
    const label = dayLabel(d.day);
    if (!d.openingTime || !d.closingTime) {
      errors.push(`${label} : renseignez l'ouverture et la fermeture.`);
      return;
    }
    if (toMinutes(d.openingTime) >= toMinutes(d.closingTime)) {
      errors.push(`${label} : la fermeture doit être après l'ouverture.`);
    }
    if (d.breakStart || d.breakEnd) {
      if (!d.breakStart || !d.breakEnd) {
        errors.push(
          `${label} : renseignez le début et la fin de la pause, ou supprimez-la.`,
        );
      } else if (toMinutes(d.breakStart) >= toMinutes(d.breakEnd)) {
        errors.push(`${label} : la fin de la pause doit être après son début.`);
      } else if (
        toMinutes(d.breakStart) < toMinutes(d.openingTime) ||
        toMinutes(d.breakEnd) > toMinutes(d.closingTime)
      ) {
        errors.push(
          `${label} : la pause doit être comprise dans les heures d'ouverture.`,
        );
      }
    }
  });
  if (!schedule.some((d) => d.isOpen)) {
    errors.push("Le salon doit être ouvert au moins un jour par semaine.");
  }
  return errors;
};

const salonToForm = (salon) => ({
  designation: salon.designation || "",
  maxDailyBookings: salon.maxDailyBookings || 0,
  maxHourlyBookings: salon.maxHourlyBookings || 0,
  slotDuration: salon.slotDuration || 0,
  weeklySchedule: normalizeSchedule(salon.weeklySchedule),
  closedDays: (salon.closedDays || []).filter((d) =>
    /^\d{4}-\d{2}-\d{2}$/.test(d),
  ),
});

export default function SalonPage() {
  const { user } = useAuthStore();
  const canManage = isManager(user);
  const { currentSalon, isLoadingSalon, getSalonById, updateSalon } =
    useSalonStore();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    designation: "",
    maxDailyBookings: 0,
    maxHourlyBookings: 0,
    slotDuration: 0,
    weeklySchedule: normalizeSchedule([]),
    closedDays: [],
  });
  const [scheduleErrors, setScheduleErrors] = useState([]);

  const [specificDate, setSpecificDate] = useState("");

  useEffect(() => {
    getSalonById(1);
  }, []);

  useEffect(() => {
    if (currentSalon) setFormData(salonToForm(currentSalon));
  }, [currentSalon]);

  const handleEditToggle = () => {
    if (isEditing) setFormData(salonToForm(currentSalon));
    setScheduleErrors([]);
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    const errors = validateSchedule(formData.weeklySchedule);
    setScheduleErrors(errors);
    if (errors.length) return;

    try {
      await updateSalon(1, {
        designation: formData.designation,
        maxDailyBookings: formData.maxDailyBookings,
        maxHourlyBookings: formData.maxHourlyBookings,
        slotDuration: formData.slotDuration,
        weeklySchedule: formData.weeklySchedule.map((d) => ({
          ...d,
          breakStart: d.breakStart || null,
          breakEnd: d.breakEnd || null,
        })),
        closedDays: formData.closedDays,
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du salon:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNumberInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: parseInt(value) || 0,
    }));
  };

  const updateDay = (day, changes) => {
    setFormData((prev) => ({
      ...prev,
      weeklySchedule: prev.weeklySchedule.map((d) =>
        d.day === day ? { ...d, ...changes } : d,
      ),
    }));
  };

  // Recopie les horaires d'un jour sur tous les autres jours ouverts
  const copyToOpenDays = (day) => {
    const source = formData.weeklySchedule.find((d) => d.day === day);
    setFormData((prev) => ({
      ...prev,
      weeklySchedule: prev.weeklySchedule.map((d) =>
        d.isOpen && d.day !== day
          ? {
              ...d,
              openingTime: source.openingTime,
              closingTime: source.closingTime,
              breakStart: source.breakStart,
              breakEnd: source.breakEnd,
            }
          : d,
      ),
    }));
  };

  const handleClosedDaysChange = (days) => {
    setFormData((prev) => ({
      ...prev,
      closedDays: days,
    }));
  };

  const addSpecificDate = () => {
    if (specificDate) {
      if (!formData.closedDays.includes(specificDate)) {
        handleClosedDaysChange([...formData.closedDays, specificDate].sort());
        setSpecificDate("");
      } else {
        alert("Cette date est déjà dans la liste des jours fermés");
      }
    }
  };

  const removeClosedDay = (dayToRemove) => {
    handleClosedDaysChange(
      formData.closedDays.filter((day) => day !== dayToRemove),
    );
  };

  const closedSpecialDates = isEditing
    ? formData.closedDays
    : salonToForm(currentSalon || {}).closedDays;
  const savedSchedule = normalizeSchedule(currentSalon?.weeklySchedule);
  if (isLoadingSalon) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin h-8 w-8 border-b-2 border-gray-900"></div>
        <span className="ml-3 font-nunito text-[14px] text-text">
          Chargement du salon...
        </span>
      </div>
    );
  }

  if (!currentSalon) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <FiMapPin size={48} className="mx-auto" />
        </div>
        <h3 className="font-poppins font-semibold text-[18px] text-title mb-2">
          Aucun salon trouvé
        </h3>
        <p className="font-nunito text-[14px] text-text">
          Le salon n'a pas encore été configuré.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* En-tête principal */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-6 gap-4">
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
            <h2 className="font-poppins font-semibold text-[18px] text-title">
              Mon Salon
            </h2>
            <span
              className={`px-3 py-1 font-nunito text-[14px] font-semibold text-action flex items-center gap-1 ${
                currentSalon.status
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              <FiInfo size={14} />
              {currentSalon.status ? "Actif" : "Inactif"}
            </span>
          </div>
          <p className="font-nunito text-[14px] text-text">
            Gérez les paramètres de votre salon : horaires de chaque jour,
            pauses, créneaux et fermetures exceptionnelles.
          </p>
        </div>

        <div className="flex gap-3 w-full lg:w-auto">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                className="flex items-center justify-center gap-2 font-nunito text-[14px] font-semibold text-action px-4 py-2 bg-green-600 text-white font-medium hover:bg-green-700 transition-colors rounded-lg shadow-sm"
              >
                <FiSave size={16} />
                Enregistrer
              </button>
              <button
                onClick={handleEditToggle}
                className="flex items-center justify-center gap-2 font-nunito text-[14px] font-semibold text-action px-4 py-2 bg-gray-500 text-white font-medium hover:bg-gray-600 transition-colors rounded-lg shadow-sm"
              >
                <FiX size={16} />
                Annuler
              </button>
            </>
          ) : canManage ? (
            <button
              onClick={handleEditToggle}
              className="flex items-center justify-center gap-2 font-nunito text-[14px] font-semibold text-action px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium hover:from-blue-700 hover:to-purple-700 transition-colors rounded-lg shadow-sm"
            >
              <FiEdit3 size={16} />
              Modifier le salon
            </button>
          ) : null}
        </div>
      </div>

      {/* Carte du salon */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 px-6 py-4 bg-gradient-to-r from-gray-50 to-white">
          <h3 className="font-poppins font-semibold text-[18px] text-title flex items-center gap-2">
            <FiMapPin className="text-blue-600" />
            Paramètres du Salon
          </h3>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Colonne gauche - Informations générales */}
            <div className="space-y-6">
              {/* Désignation */}
              <div>
                <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2 flex items-center gap-2">
                  <FiMapPin size={16} className="text-gray-400" />
                  Nom du salon
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.designation}
                    onChange={(e) =>
                      handleInputChange("designation", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-nunito text-[14px]"
                    placeholder="Nom de votre salon"
                  />
                ) : (
                  <p className="font-poppins font-semibold text-[18px] text-title bg-gray-50 px-3 py-2 rounded-lg">
                    {currentSalon.designation}
                  </p>
                )}
              </div>

            </div>

            {/* Colonne droite - Capacités et fermetures */}
            <div className="space-y-6">
              {/* Capacités de réservation */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2 flex items-center gap-2">
                    <FiUsers size={16} className="text-gray-400" />
                    Réservations/jour
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      value={formData.maxDailyBookings}
                      onChange={(e) =>
                        handleNumberInputChange(
                          "maxDailyBookings",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-nunito text-[14px]"
                      min="1"
                    />
                  ) : (
                    <p className="font-poppins font-semibold text-[18px] text-title bg-gray-50 px-3 py-2 rounded-lg">
                      {currentSalon.maxDailyBookings}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2 flex items-center gap-2">
                    <FiUser size={16} className="text-gray-400" />
                    Réservations/heure
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      value={formData.maxHourlyBookings}
                      onChange={(e) =>
                        handleNumberInputChange(
                          "maxHourlyBookings",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-nunito text-[14px]"
                      min="1"
                    />
                  ) : (
                    <p className="font-poppins font-semibold text-[18px] text-title bg-gray-50 px-3 py-2 rounded-lg">
                      {currentSalon.maxHourlyBookings}
                    </p>
                  )}
                </div>
              </div>

              {/* Durée des créneaux */}
              <div>
                <label className="block font-poppins font-medium text-[15px] text-subtitle mb-2 flex items-center gap-2">
                  <FiWatch size={16} className="text-gray-400" />
                  Durée des créneaux
                </label>
                {isEditing ? (
                  <input
                    type="number"
                    value={formData.slotDuration}
                    onChange={(e) =>
                      handleNumberInputChange("slotDuration", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-nunito text-[14px]"
                    min="15"
                    step="15"
                  />
                ) : (
                  <p className="font-poppins font-semibold text-[18px] text-title bg-gray-50 px-3 py-2 rounded-lg">
                    {currentSalon.slotDuration} minutes
                  </p>
                )}
              </div>

              {/* Fermetures exceptionnelles */}
              <div>
                <label className="block font-poppins font-medium text-[15px] text-subtitle mb-3 flex items-center gap-2">
                  <FiCalendar size={16} className="text-gray-400" />
                  Fermetures exceptionnelles
                </label>
                {isEditing && (
                  <div className="flex gap-2 mb-3">
                    <DatePicker
                      value={specificDate}
                      onChange={setSpecificDate}
                      placeholder="Choisir une date"
                    />
                    <button
                      type="button"
                      onClick={addSpecificDate}
                      disabled={!specificDate}
                      className="px-3 py-2 bg-blue-600 text-white font-nunito text-[14px] font-semibold text-action rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                    >
                      <FiPlus size={14} />
                      Ajouter
                    </button>
                  </div>
                )}
                {closedSpecialDates.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {closedSpecialDates.map((date) => (
                      <span
                        key={date}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 font-nunito text-[14px] border border-purple-200 rounded-lg"
                      >
                        {formatSpecialDate(date)}
                        {isEditing && (
                          <button
                            type="button"
                            onClick={() => removeClosedDay(date)}
                            className="text-purple-600 hover:text-red-600 transition-colors"
                            title="Retirer cette date"
                          >
                            <FiTrash2 size={12} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="font-nunito text-[14px] text-text bg-gray-50 px-3 py-2 rounded-lg">
                    Aucune fermeture exceptionnelle
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Horaires par jour */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h4 className="font-poppins font-semibold text-[18px] text-title mb-1 flex items-center gap-2">
              <FiClock className="text-blue-600" />
              Horaires par jour
            </h4>
            <p className="font-nunito text-[14px] text-text mb-4">
              Les clients ne peuvent réserver que pendant les heures
              d'ouverture du jour, en dehors de la pause.
            </p>

            {isEditing ? (
              <div className="space-y-3">
                {scheduleErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 font-nunito text-[14px]">
                    {scheduleErrors.map((error) => (
                      <p key={error}>{error}</p>
                    ))}
                  </div>
                )}
                {formData.weeklySchedule.map((d) => {
                  const inputClass =
                    "w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-nunito text-[14px]";
                  return (
                    <div
                      key={d.day}
                      className={`p-3 rounded-lg border ${
                        d.isOpen ? "border-gray-200" : "border-red-200 bg-red-50"
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <span className="font-poppins font-medium text-[15px] text-subtitle w-24">
                          {dayLabel(d.day)}
                        </span>
                        <label className="flex items-center gap-2 font-nunito text-[14px] text-text">
                          <input
                            type="checkbox"
                            checked={d.isOpen}
                            onChange={(e) =>
                              updateDay(d.day, { isOpen: e.target.checked })
                            }
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          {d.isOpen ? "Ouvert" : "Fermé"}
                        </label>
                        {d.isOpen && (
                          <button
                            type="button"
                            onClick={() => copyToOpenDays(d.day)}
                            className="font-nunito text-[13px] text-blue-600 hover:underline"
                          >
                            Appliquer à tous les jours ouverts
                          </button>
                        )}
                      </div>
                      {d.isOpen && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <label className="font-nunito text-[13px] text-text">
                            Ouverture
                            <input
                              type="time"
                              value={d.openingTime}
                              onChange={(e) =>
                                updateDay(d.day, { openingTime: e.target.value })
                              }
                              className={inputClass}
                            />
                          </label>
                          <label className="font-nunito text-[13px] text-text">
                            Fermeture
                            <input
                              type="time"
                              value={d.closingTime}
                              onChange={(e) =>
                                updateDay(d.day, { closingTime: e.target.value })
                              }
                              className={inputClass}
                            />
                          </label>
                          <label className="font-nunito text-[13px] text-text">
                            Début pause
                            <input
                              type="time"
                              value={d.breakStart}
                              onChange={(e) =>
                                updateDay(d.day, { breakStart: e.target.value })
                              }
                              className={inputClass}
                            />
                          </label>
                          <label className="font-nunito text-[13px] text-text">
                            Fin pause
                            <input
                              type="time"
                              value={d.breakEnd}
                              onChange={(e) =>
                                updateDay(d.day, { breakEnd: e.target.value })
                              }
                              className={inputClass}
                            />
                          </label>
                          {(d.breakStart || d.breakEnd) && (
                            <button
                              type="button"
                              onClick={() =>
                                updateDay(d.day, { breakStart: "", breakEnd: "" })
                              }
                              className="col-span-2 sm:col-span-4 text-left font-nunito text-[13px] text-red-600 hover:underline"
                            >
                              Supprimer la pause
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 font-nunito text-[14px]">
                {savedSchedule.map((d) => (
                  <div
                    key={d.day}
                    className={`p-3 rounded-lg border ${
                      d.isOpen
                        ? "bg-green-50 border-green-200"
                        : "bg-red-50 border-red-200"
                    }`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-poppins font-medium text-[15px] text-subtitle">
                        {dayLabel(d.day)}
                      </span>
                      <span
                        className={`px-2 py-1 font-nunito text-[14px] font-semibold text-action ${
                          d.isOpen
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {d.isOpen ? "Ouvert" : "Fermé"}
                      </span>
                    </div>
                    {d.isOpen && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <FiClock size={12} />
                          {d.openingTime} - {d.closingTime}
                        </div>
                        <div className="flex items-center gap-1">
                          <FiPauseCircle size={12} />
                          {d.breakStart
                            ? `Pause : ${d.breakStart} - ${d.breakEnd}`
                            : "Pas de pause"}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Informations de création/mise à jour */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 font-nunito text-[14px] text-text">
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-xl border border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <FiCalendar className="text-blue-600" />
            <span className="font-poppins font-medium text-[15px] text-subtitle">
              Créé le
            </span>
          </div>
          {formatFrenchDateCapitalized(currentSalon.createdAt)}
        </div>
        <div className="bg-gradient-to-r from-gray-50 to-green-50 p-4 rounded-xl border border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <FiEdit3 className="text-green-600" />
            <span className="font-poppins font-medium text-[15px] text-subtitle">
              Dernière modification
            </span>
          </div>
          {formatFrenchDateCapitalized(currentSalon.updatedAt)}
        </div>
      </div>
    </div>
  );
}
